import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { forkJoin } from 'rxjs';
import { PortalNavbarComponent } from './portal-navbar.component';
import {
  TeamDto,
  TeamPlayerCreateDto,
  TeamPlayerMaintenanceDto,
  TeamPlayerUpdateDto,
  TeamService
} from '../services/team.service';
import { AuthService } from '../services/auth.service';

interface PlayerDraft {
  id: number;
  teamId: number;
  userId?: number;
  firstName: string;
  lastName: string;
  order: number;
  enableCustomSound: boolean;
  enableCustomVideo: boolean;
  isEnabled: boolean;
  isDeleted: boolean;
}

@Component({
  selector: 'app-players-maintenance-page',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, PortalNavbarComponent],
  template: `
    <div class="page">
      <app-portal-navbar></app-portal-navbar>

      <section class="page-hero">
        <div class="page-hero-inner">
          <a routerLink="/home" class="home-btn">Home</a>
          <h1 class="page-title">Utilities · Players</h1>
          <p class="page-sub">Central player maintenance table for SiteAdmin and OrgAdmin.</p>
        </div>
      </section>

      <section class="page-body">
        <div class="page-body-inner">
          @if (message()) {
            <div class="status-banner">{{ message() }}</div>
          }

          <div class="card">
            <div class="card-hd">
              <span class="card-title">Players</span>
              <div class="card-tools">
                <input class="tool-input" placeholder="Search players…" [(ngModel)]="filterText" />
                <select class="tool-select" [(ngModel)]="statusFilter">
                  <option value="all">All</option>
                  <option value="active">Active</option>
                  <option value="disabled">Disabled</option>
                  <option value="deleted">Deleted</option>
                </select>
                <span class="card-count">{{ filteredPlayers().length }} player{{ filteredPlayers().length !== 1 ? 's' : '' }}</span>
              </div>
            </div>

            <div class="table-wrap">
              <div class="table-head">
                <span>Name</span>
                <span>Organization</span>
                <span>Team</span>
                <span>Order</span>
                <span>Status</span>
                <span>Action</span>
              </div>

              @for (player of filteredPlayers(); track player.id) {
                <div class="table-row" [class.row--deleted]="player.isDeleted">
                  <span>{{ player.firstName }} {{ player.lastName }}</span>
                  <span>{{ player.organizationName }}</span>
                  <span>{{ player.teamName }}</span>
                  <span>{{ player.order }}</span>
                  <span>{{ statusLabel(player) }}</span>
                  <div>
                    <button class="btn btn--ghost" (click)="openEdit(player)">Edit</button>
                  </div>
                </div>
              } @empty {
                <div class="empty">No players found.</div>
              }
            </div>
          </div>

          <div class="action-bar">
            <button class="btn btn--primary" (click)="openNew()">New Player</button>
          </div>
        </div>
      </section>

      @if (isModalOpen) {
        <div class="modal-backdrop" (click)="closeModal()">
          <div class="modal" (click)="$event.stopPropagation()">
            <div class="modal-hd">
              <h2 class="modal-title">{{ isNewPlayer ? 'New Player' : 'Edit Player' }}</h2>
              <button class="modal-close" (click)="closeModal()" aria-label="Close">×</button>
            </div>

            <div class="modal-body">
              <div class="field-row">
                <div class="field">
                  <label class="field-label">First Name</label>
                  <input class="field-input" [(ngModel)]="draft.firstName" />
                </div>
                <div class="field">
                  <label class="field-label">Last Name</label>
                  <input class="field-input" [(ngModel)]="draft.lastName" />
                </div>
              </div>

              <div class="field-row">
                <div class="field">
                  <label class="field-label">Team</label>
                  <select class="field-select" [(ngModel)]="draft.teamId" [disabled]="!isNewPlayer">
                    @for (team of teams(); track team.id) {
                      <option [ngValue]="team.id">{{ team.name }}</option>
                    }
                  </select>
                </div>
                <div class="field">
                  <label class="field-label">Order</label>
                  <input type="number" min="1" class="field-input" [(ngModel)]="draft.order" />
                </div>
              </div>

              <div class="field-row">
                <label class="checkbox-field">
                  <input type="checkbox" [(ngModel)]="draft.enableCustomSound" /> Custom Sound
                </label>
                <label class="checkbox-field">
                  <input type="checkbox" [(ngModel)]="draft.enableCustomVideo" /> Custom Video
                </label>
              </div>

              @if (!isNewPlayer) {
                <div class="field-row field-row--meta">
                  <span>Status: {{ draft.isDeleted ? 'Deleted' : (draft.isEnabled ? 'Enabled' : 'Disabled') }}</span>
                  <span>User Id: {{ draft.userId ?? 'Manual' }}</span>
                </div>
              }
            </div>

            <div class="modal-ft" [class.modal-ft--end]="isNewPlayer">
              @if (!isNewPlayer) {
                <div class="modal-actions">
                  @if (!draft.isDeleted) {
                    <button class="btn btn--ghost" (click)="toggleEnabled()">{{ draft.isEnabled ? 'Disable' : 'Enable' }}</button>
                    <button class="btn btn--danger" (click)="softDelete()">Soft Delete</button>
                  }
                  @if (draft.isDeleted && isSiteAdmin()) {
                    <button class="btn btn--ghost" (click)="restore()">Restore</button>
                    <button class="btn btn--danger" (click)="hardDelete()">Hard Delete</button>
                  }
                </div>
              }

              <div class="modal-actions">
                <button class="btn btn--ghost" (click)="closeModal()">Cancel</button>
                <button class="btn btn--primary" [disabled]="draft.isDeleted" (click)="save()">Save</button>
              </div>
            </div>
          </div>
        </div>
      }
    </div>
  `,
  styles: [`
    .page { min-height: 100vh; }
    .page-hero {
      border-bottom: 1px solid var(--color-border);
      padding: 2rem 1.5rem;
      background: linear-gradient(155deg, var(--color-bg-start) 0%, var(--color-bg-end) 100%);
    }
    .page-hero-inner, .page-body-inner { max-width: 1100px; margin: 0 auto; }
    .home-btn { color: var(--color-muted); text-decoration: none; }
    .page-title { margin: .6rem 0 0; }
    .page-sub { margin: .4rem 0 0; color: var(--color-muted); }
    .page-body { padding: 1.5rem; }
    .status-banner {
      margin-bottom: .8rem;
      border: 1px solid var(--color-border);
      border-radius: .6rem;
      padding: .55rem .75rem;
    }
    .card {
      border: 1px solid var(--color-border);
      border-radius: .9rem;
      overflow: hidden;
      background: color-mix(in srgb, var(--color-panel) 80%, transparent);
    }
    .card-hd {
      display: flex;
      justify-content: space-between;
      gap: .6rem;
      flex-wrap: wrap;
      border-bottom: 1px solid var(--color-border);
      padding: .8rem 1rem;
    }
    .card-title { font-weight: 600; }
    .card-tools { display: flex; gap: .45rem; flex-wrap: wrap; align-items: center; }
    .card-count { color: var(--color-muted); font-size: .9rem; }
    .tool-input,.tool-select,.field-input,.field-select {
      border: 1px solid var(--color-border);
      border-radius: .45rem;
      background: transparent;
      color: var(--color-text);
      padding: .4rem .55rem;
    }
    .table-head,.table-row {
      display: grid;
      grid-template-columns: 1.5fr 1.3fr 1.1fr .7fr .8fr .8fr;
      gap: .75rem;
      padding: .7rem 1rem;
      align-items: center;
    }
    .table-head {
      font-size: .72rem;
      text-transform: uppercase;
      letter-spacing: .05em;
      color: var(--color-muted);
      border-bottom: 1px solid var(--color-border);
    }
    .table-row { border-bottom: 1px solid color-mix(in srgb, var(--color-border) 50%, transparent); }
    .row--deleted { opacity: .75; }
    .empty { padding: 1rem; color: var(--color-muted); }
    .action-bar { margin-top: .9rem; display: flex; justify-content: flex-end; }
    .btn {
      border: 1px solid var(--color-border);
      border-radius: .45rem;
      padding: .4rem .65rem;
      background: transparent;
      color: var(--color-text);
      cursor: pointer;
    }
    .btn--ghost { color: var(--color-muted); }
    .btn--primary {
      border-color: var(--color-accent);
      background: var(--color-accent);
      color: #fff;
    }
    .btn--danger {
      border-color: color-mix(in srgb, #ef4444 60%, var(--color-border));
      color: #fca5a5;
    }
    .modal-backdrop {
      position: fixed;
      inset: 0;
      background: rgba(0,0,0,.45);
      display: grid;
      place-items: center;
      z-index: 400;
      padding: 1rem;
    }
    .modal {
      width: min(680px, 96vw);
      border: 1px solid var(--color-border);
      border-radius: .95rem;
      background: color-mix(in srgb, var(--color-panel) 92%, black 8%);
      overflow: hidden;
    }
    .modal-hd,.modal-ft {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: .8rem 1rem;
      border-bottom: 1px solid var(--color-border);
      gap: .6rem;
      flex-wrap: wrap;
    }
    .modal-ft { border-top: 1px solid var(--color-border); border-bottom: 0; }
    .modal-body { padding: .9rem 1rem; display: grid; gap: .8rem; }
    .modal-title { margin: 0; font-size: 1rem; }
    .modal-close {
      border: 0;
      background: transparent;
      color: var(--color-muted);
      font-size: 1.2rem;
      cursor: pointer;
    }
    .field { display: grid; gap: .35rem; flex: 1; }
    .field-row { display: flex; gap: .7rem; flex-wrap: wrap; }
    .field-row--meta { color: var(--color-muted); font-size: .85rem; }
    .field-label { color: var(--color-muted); font-size: .82rem; }
    .checkbox-field { color: var(--color-muted); display: inline-flex; gap: .35rem; align-items: center; }
    .modal-actions { display: flex; gap: .45rem; flex-wrap: wrap; }
  `]
})
export class PlayersMaintenancePageComponent implements OnInit {
  private readonly teamService = inject(TeamService);
  private readonly authService = inject(AuthService);

  readonly players = signal<TeamPlayerMaintenanceDto[]>([]);
  readonly teams = signal<TeamDto[]>([]);
  readonly message = signal<string>('');
  readonly isSiteAdmin = signal(false);

  isModalOpen = false;
  isNewPlayer = false;

  filterText = '';
  statusFilter: 'all' | 'active' | 'disabled' | 'deleted' = 'all';

  draft: PlayerDraft = this.newDraft();

  ngOnInit(): void {
    void this.initializeAsync();
  }

  filteredPlayers(): TeamPlayerMaintenanceDto[] {
    const q = this.filterText.trim().toLowerCase();
    const filtered = this.players().filter(p => {
      if (this.statusFilter === 'deleted' && !p.isDeleted) {
        return false;
      }

      if (this.statusFilter === 'active' && (p.isDeleted || !p.isEnabled)) {
        return false;
      }

      if (this.statusFilter === 'disabled' && (p.isDeleted || p.isEnabled)) {
        return false;
      }

      if (!q) {
        return true;
      }

      return (`${p.firstName} ${p.lastName}`).toLowerCase().includes(q)
        || p.organizationName.toLowerCase().includes(q)
        || p.teamName.toLowerCase().includes(q);
    });

    return filtered.sort((a, b) => `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`));
  }

  statusLabel(player: TeamPlayerMaintenanceDto): string {
    if (player.isDeleted) {
      return 'Deleted';
    }

    return player.isEnabled ? 'Enabled' : 'Disabled';
  }

  openEdit(player: TeamPlayerMaintenanceDto): void {
    this.isNewPlayer = false;
    this.draft = {
      id: player.id,
      teamId: player.teamId,
      userId: player.userId,
      firstName: player.firstName,
      lastName: player.lastName,
      order: player.order,
      enableCustomSound: player.enableCustomSound,
      enableCustomVideo: player.enableCustomVideo,
      isEnabled: player.isEnabled,
      isDeleted: player.isDeleted
    };
    this.isModalOpen = true;
  }

  openNew(): void {
    const firstTeamId = this.teams()[0]?.id;
    if (!firstTeamId) {
      this.message.set('Create an active team before adding players.');
      return;
    }

    this.isNewPlayer = true;
    this.draft = {
      ...this.newDraft(),
      teamId: firstTeamId
    };
    this.isModalOpen = true;
  }

  closeModal(): void {
    this.isModalOpen = false;
    this.isNewPlayer = false;
    this.draft = this.newDraft();
  }

  save(): void {
    if (!this.draft.firstName.trim() || !this.draft.lastName.trim()) {
      this.message.set('First name and last name are required.');
      return;
    }

    if (this.isNewPlayer) {
      const createDto: TeamPlayerCreateDto = {
        teamId: this.draft.teamId,
        userId: this.draft.userId,
        firstName: this.draft.firstName.trim(),
        lastName: this.draft.lastName.trim(),
        order: Math.max(1, this.draft.order),
        enableCustomSound: this.draft.enableCustomSound,
        enableCustomVideo: this.draft.enableCustomVideo
      };

      this.teamService.createPlayer(createDto).subscribe({
        next: () => {
          this.message.set('Player created.');
          this.closeModal();
          this.loadData();
        },
        error: () => this.message.set('Unable to create player.')
      });

      return;
    }

    const updateDto: TeamPlayerUpdateDto = {
      userId: this.draft.userId,
      firstName: this.draft.firstName.trim(),
      lastName: this.draft.lastName.trim(),
      order: Math.max(1, this.draft.order),
      enableCustomSound: this.draft.enableCustomSound,
      enableCustomVideo: this.draft.enableCustomVideo
    };

    this.teamService.updatePlayer(this.draft.id, updateDto).subscribe({
      next: () => {
        this.message.set('Player updated.');
        this.closeModal();
        this.loadData();
      },
      error: () => this.message.set('Unable to update player.')
    });
  }

  toggleEnabled(): void {
    this.teamService.setPlayerEnabled(this.draft.id, !this.draft.isEnabled).subscribe({
      next: () => {
        this.message.set(this.draft.isEnabled ? 'Player disabled.' : 'Player enabled.');
        this.closeModal();
        this.loadData();
      },
      error: () => this.message.set('Unable to update player status.')
    });
  }

  softDelete(): void {
    this.teamService.deletePlayer(this.draft.id).subscribe({
      next: () => {
        this.message.set('Player soft deleted.');
        this.closeModal();
        this.loadData();
      },
      error: () => this.message.set('Unable to delete player.')
    });
  }

  restore(): void {
    this.teamService.restorePlayer(this.draft.id).subscribe({
      next: () => {
        this.message.set('Player restored.');
        this.closeModal();
        this.loadData();
      },
      error: () => this.message.set('Unable to restore player.')
    });
  }

  hardDelete(): void {
    this.teamService.hardDeletePlayer(this.draft.id).subscribe({
      next: () => {
        this.message.set('Player hard deleted.');
        this.closeModal();
        this.loadData();
      },
      error: () => this.message.set('Unable to hard delete player.')
    });
  }

  private async initializeAsync(): Promise<void> {
    const roles = await this.authService.getRolesAsync();
    this.isSiteAdmin.set(roles.includes('SiteAdmin'));
    this.loadData();
  }

  private loadData(): void {
    forkJoin({
      players: this.teamService.getAllPlayersForMaintenance(),
      teams: this.teamService.getAll()
    }).subscribe({
      next: ({ players, teams }) => {
        this.players.set(players);
        this.teams.set(teams.filter(t => !t.isDeleted));
      },
      error: () => this.message.set('Unable to load player maintenance data.')
    });
  }

  private newDraft(): PlayerDraft {
    return {
      id: 0,
      teamId: 0,
      userId: undefined,
      firstName: '',
      lastName: '',
      order: 1,
      enableCustomSound: false,
      enableCustomVideo: false,
      isEnabled: true,
      isDeleted: false
    };
  }
}
